import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ParamMap } from '@angular/router';
import { CustomColors, VendorSite } from 'src/app/model/vendorSite';

export type Brand = 'vendor' | 'igive';

interface ThemeColors {
  primary: string;
  displayName: string;
  border: string;
  btn: string;
  textContrast: string;
  rgb: string;
  accent: string;
}

const IGIVE_COLORS: ThemeColors = {
  primary: '#F3D7B6',
  displayName: '#B47c3c',
  border: '#000',
  btn: '#F3D7B6',
  textContrast: '#222',
  rgb: '243, 215, 182',
  // Brand chrome (tints, borders, thin lines, small text) needs an ink that stays
  // visible on white. iGive's box color is a pale beige, so the accent is the darker
  // gold display color rather than the box color itself.
  accent: '#B47c3c',
};

// A vendor color lighter than this (per the luminance formula below) washes out any
// UI derived from it on a white surface. computeAccent darkens such colors to this
// target; darker colors pass through unchanged.
const ACCENT_MAX_LUMINANCE = 0.55;

const IGIVE_TITLE_SUFFIX = 'Gavekortbutikk';
const IGIVE_TITLE = `iGive ${IGIVE_TITLE_SUFFIX}`;
const IGIVE_FAVICON = 'assets/icon/favicon.png';
const IGIVE_THEME_COLOR = IGIVE_COLORS.primary;

// The official iGive gift-card mark (from favicon.svg / mine-gavekort-app-icon.svg),
// reused for vendor favicons recolored to the vendor hue.
const GIFT_PATH = 'M906.4,402.4l-12.65-5.11V319.76a57.12,57.12,0,0,0-57.13-57.12H560.48l-256-103.46a57.09,57.09,0,0,0-74.37,31.57l-29,71.89H187.38a57.12,57.12,0,0,0-57.13,57.12V437.81L86,547.23A57.12,57.12,0,0,0,117.6,621.6l12.65,5.11v77.52a57.13,57.13,0,0,0,57.13,57.13H463.53l256,103.45a57.05,57.05,0,0,0,74.36-31.56L823,761.36h13.64a57.13,57.13,0,0,0,57.13-57.13v-118L938,476.77A57.14,57.14,0,0,0,906.4,402.4ZM130.25,600.23l-3.46-1.39a32.59,32.59,0,0,1-18-42.41l21.46-53.11ZM252.82,200a32.53,32.53,0,0,1,42.42-18L495,262.64H227.49Zm-98,119.81a32.62,32.62,0,0,1,32.59-32.58H341.19V426.84c-3.84-2-7.29-3.25-10.07-5.38-23.3-17.89-50.76-25.37-78.75-31.31-11.71-2.5-17.36,1.17-20.69,12.62-2.06,7-3.37,14.3-4.75,21.51l-1,4.28-.61,2.57H154.79ZM341.63,736.82H187.38a32.63,32.63,0,0,1-32.59-32.59V485.62H264.32s-12.86,14.36-19,19.59c-3.55,3-6.71,5.91-7.82,9.59-.88,2.93,1.36,17,1.68,21.2,1.34,17.4-.28,43,.06,62.81,0,2.19,0,7.42,0,7.42l102.4-104.75Zm429.55,87.23a32.53,32.53,0,0,1-42.42,18L529.05,761.36H796.51Zm98-119.82a32.62,32.62,0,0,1-32.58,32.59H397.41V492.67C434,532.51,506.27,612.5,506.27,612.5S508.2,591.11,509,581c1.34-16.51,2.41-33,4-49.52.58-6-.94-10.49-5.37-14.73-9.79-9.36-20.8-20.87-30.93-31.11H869.2Zm0-273.07H516.46l-1.75-3.87-.06-.1a11.86,11.86,0,0,1-1-2.29,145.57,145.57,0,0,1-10.69-32.51c-2.7-8.61-4-9.94-13.11-9.43-14.26.82-27.93,4.48-39.92,12-15.17,9.46-48.79,34.1-52.15,36.63V287.18H836.62a32.61,32.61,0,0,1,32.58,32.58Zm46,36.41-21.45,53.11V423.76l3.45,1.4a32.57,32.57,0,0,1,18,42.41ZM727.6,637.64a42.87,42.87,0,0,1,54.91-51.25,24.87,24.87,0,0,1,14-26.09,72.32,72.32,0,0,0-91.69,100.1ZM786.42,621l-21.85,21.79H792.3v20.47a42.88,42.88,0,0,1-47-.49l-21.13,21.12a72.29,72.29,0,0,0,95.46-4.93l2.22-2.27V615.1H792.3ZM790.65,583a16.41,16.41,0,1,0,19.53-16.11,16.84,16.84,0,0,0-3.12-.3A16.41,16.41,0,0,0,790.65,583Z';

// Gradient envelope for the gift sheen: [offset, lighten/darken amount]. Mirrors the
// original gold gradient's dark-light-dark metallic curve, applied to the vendor hue.
const GIFT_SHEEN: ReadonlyArray<readonly [number, number]> = [
  [0, -0.05], [0.13, 0.08], [0.38, 0.28], [0.51, 0.42], [0.62, 0.3], [0.8, 0.1], [1, -0.05],
];

// iOS home-screen icons are 180x180 at @1x; we rasterize the SVG mark to this size.
const APPLE_TOUCH_ICON_SIZE = 180;

@Injectable({ providedIn: 'root' })
export class BrandService {

  // Bumped on every favicon change so a slow async rasterization from a previous
  // brand can detect it has been superseded and skip its stale write (see
  // setAppleTouchIcon).
  private iconGeneration = 0;

  constructor(private title: Title) {}

  resolveBrand(params: ParamMap | null): Brand {
    if (params?.get('brand') === 'igive') return 'igive';
    return 'vendor';
  }

  apply(brand: Brand, vendorSite: VendorSite | null): Brand {
    const resolved: Brand = brand === 'vendor' && vendorSite ? 'vendor' : 'igive';
    if (resolved === 'vendor') {
      this.applyVendor(vendorSite as VendorSite);
    } else {
      this.applyIGive();
    }
    return resolved;
  }

  // Pages that own the brand-determining context (customize-card) call this
  // after apply() so later pages without that context (distribution) can
  // recover the user's brand from sessionStorage.
  rememberBrand(brand: Brand): void {
    sessionStorage.setItem('brand', brand);
  }

  recallBrand(): Brand | null {
    const stored = sessionStorage.getItem('brand');
    return stored === 'vendor' || stored === 'igive' ? stored : null;
  }

  // Repaints ONLY the vendor color CSS variables from a store's customColors — no
  // title, favicon, or meta changes. For pages that reach the user after the DIBS
  // full-page redirect has wiped the :root inline vars (receipt) and that only need
  // the brand accent to render true, not the document chrome. No-ops for the 'igive'
  // brand (the app-bootstrap baseline already paints it) and when customColors is absent.
  applyStoreColors(brand: Brand, colors: CustomColors | null | undefined): void {
    if (brand !== 'vendor' || !colors?.boxColor) return;
    this.setColors(this.vendorThemeColors(colors));
  }

  private applyVendor(vs: VendorSite): void {
    const colors = vs.customColors;
    // The whole palette is derived from boxColor, and the admin lets a vendorsite
    // save customColors without one (6 of the 24 Scala centres have boxColor null).
    // Gate on the value rather than the object: passing null through to the hex
    // parsers threw and aborted the rest of the brand apply.
    if (colors?.boxColor) {
      this.setColors(this.vendorThemeColors(colors));
    }

    const displayName = vs.displayName || vs.vendorName || '';
    this.title.setTitle(displayName ? `${displayName} ${IGIVE_TITLE_SUFFIX}` : IGIVE_TITLE);
    const tileBg = colors?.boxColor || IGIVE_COLORS.primary;
    this.setFavicon(this.buildGiftFavicon(tileBg));
    this.setMeta('theme-color', colors?.boxColor || IGIVE_THEME_COLOR);
    this.setMeta('description', vs.ingress || vs.slogan || '');
    this.setMetaProperty('og:title', displayName ? `${displayName} ${IGIVE_TITLE_SUFFIX}` : IGIVE_TITLE);
    if (vs.imageUrl) this.setMetaProperty('og:image', vs.imageUrl);
  }

  private applyIGive(): void {
    this.setColors(IGIVE_COLORS);
    this.title.setTitle(IGIVE_TITLE);
    this.setFavicon(IGIVE_FAVICON);
    this.setMeta('theme-color', IGIVE_THEME_COLOR);
    this.setMeta('description', '');
    this.setMetaProperty('og:title', IGIVE_TITLE);
  }

  // Derives the full brand palette from a vendor's box/text colors. Shared by the
  // full vendor apply and the color-only applyStoreColors path.
  private vendorThemeColors(colors: CustomColors): ThemeColors {
    return {
      primary: colors.boxColor,
      displayName: colors.textColor,
      border: colors.boxColor,
      btn: colors.boxColor,
      textContrast: this.computeTextContrast(colors.boxColor),
      rgb: this.computeRgbString(colors.boxColor),
      accent: this.computeAccent(colors.boxColor),
    };
  }

  private setColors(c: ThemeColors): void {
    const root = document.documentElement.style;
    root.setProperty('--ion-color-primary', c.primary);
    root.setProperty('--igive-color-displayname', c.displayName);
    root.setProperty('--igive-color-border', c.border);
    root.setProperty('--igive-color-btn', c.btn);
    root.setProperty('--vendor-color', c.btn);
    root.setProperty('--vendor-color-rgb', c.rgb);
    root.setProperty('--vendor-accent', c.accent);
    root.setProperty('--igive-color-text-contrast', c.textContrast);
  }

  // Sets the tab favicon. SVG data URIs and static PNG paths are both accepted; the
  // MIME type is sniffed from the href. The iOS home-screen icon is handled
  // separately because it cannot use SVG (see setAppleTouchIcon).
  private setFavicon(href: string): void {
    const generation = ++this.iconGeneration;
    const type = href.startsWith('data:image/svg') ? 'image/svg+xml' : 'image/png';
    document.querySelectorAll('link[rel="icon"]').forEach(el => {
      const link = el as HTMLLinkElement;
      link.href = href;
      link.type = type;
    });
    this.setAppleTouchIcon(href, generation);
  }

  // iOS ignores SVG apple-touch-icons (it requires a raster PNG), so when the
  // favicon is a dynamic SVG mark we rasterize it to a PNG via <canvas> and swap it
  // in once ready. Static PNG hrefs are applied directly. Rasterization is async and
  // best-effort: if the canvas/image pipeline is unavailable the existing PNG link
  // is left in place rather than pointing iOS at an SVG it won't render.
  //
  // Rasterization is async, so a rapid brand switch can resolve an older SVG after a
  // newer brand has already been applied. The generation token guards against that:
  // a resolved rasterization only writes if it is still the current favicon.
  private setAppleTouchIcon(href: string, generation: number): void {
    const links = document.querySelectorAll('link[rel="apple-touch-icon"]');
    if (!links.length) return;

    if (!href.startsWith('data:image/svg')) {
      links.forEach(el => {
        const link = el as HTMLLinkElement;
        link.href = href;
        link.type = 'image/png';
      });
      return;
    }

    this.rasterizeSvg(href, APPLE_TOUCH_ICON_SIZE).then(png => {
      if (!png || generation !== this.iconGeneration) return;
      links.forEach(el => {
        const link = el as HTMLLinkElement;
        link.href = png;
        link.type = 'image/png';
      });
    });
  }

  // Draws an SVG data URI onto an offscreen square canvas and returns a PNG data
  // URI, or null if the image fails to load or the canvas cannot be encoded.
  private rasterizeSvg(svgHref: string, size: number): Promise<string | null> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, size, size);
        try {
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = svgHref;
    });
  }

  // Vendor marketing logos render poorly when squeezed into a 16-32px favicon, so
  // we render the official iGive gift mark instead: it sits on a flat vendor-color
  // tile, filled with a metallic sheen in the auto-contrast color (white on dark
  // vendor colors, dark on light ones so it stays visible) over a soft drop shadow.
  // Inline SVG, crisp at any size.
  private buildGiftFavicon(bgColor: string): string {
    const fg = this.computeTextContrast(bgColor);
    const stops = GIFT_SHEEN
      .map(([offset, amount]) => `<stop offset="${offset}" stop-color="${shadeColor(fg, amount)}"/>`)
      .join('');
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="1024" height="1024" viewBox="0 0 1024 1024">` +
      `<defs>` +
      `<linearGradient id="g" x1="63.04" y1="209.24" x2="876.42" y2="757.75" gradientUnits="userSpaceOnUse">` +
      stops +
      `</linearGradient>` +
      `<filter id="s" x="-20%" y="-20%" width="140%" height="140%">` +
      `<feDropShadow dx="0" dy="10" stdDeviation="14" flood-color="#000" flood-opacity="0.28"/>` +
      `</filter>` +
      `</defs>` +
      `<rect width="1024" height="1024" rx="220.8" fill="${bgColor}"/>` +
      `<path fill="url(#g)" filter="url(#s)" d="${GIFT_PATH}"/>` +
      `</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }

  private setMeta(name: string, content: string): void {
    let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  }

  private setMetaProperty(property: string, content: string): void {
    let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('property', property);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  }

  private computeRgbString(hex: string): string {
    const rgb = hexToRgb(hex);
    return rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : '';
  }

  private computeTextContrast(hex: string): string {
    const rgb = hexToRgb(hex);
    if (!rgb) return IGIVE_COLORS.textContrast;
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return luminance > 0.5 ? '#333' : '#fff';
  }

  // A brand-tinted accent that stays legible as ink, borders, and thin lines on a
  // white surface. Colors darker than the target luminance are used as-is; lighter
  // ones (e.g. a pale beige box color) are scaled down uniformly, which lowers
  // luminance to the target while preserving the hue.
  private computeAccent(hex: string): string {
    const rgb = hexToRgb(hex);
    if (!rgb) return IGIVE_COLORS.accent;
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    if (luminance <= ACCENT_MAX_LUMINANCE) return hex;
    const scale = ACCENT_MAX_LUMINANCE / luminance;
    const r = Math.round(rgb.r * scale);
    const g = Math.round(rgb.g * scale);
    const b = Math.round(rgb.b * scale);
    return `rgb(${r}, ${g}, ${b})`;
  }
}

// Blends a hex color toward white (amount > 0) or black (amount < 0) by the given
// fraction, returning an rgb() string. Falls back to the input on unparseable hex.
function shadeColor(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const target = amount < 0 ? 0 : 255;
  const fraction = Math.abs(amount);
  const blend = (c: number) => Math.round(c + (target - c) * fraction);
  return `rgb(${blend(rgb.r)}, ${blend(rgb.g)}, ${blend(rgb.b)})`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let value = hex.replace(/^#/, '');
  if (value.length === 3) value = value.split('').map(c => c + c).join('');
  if (value.length !== 6) return null;
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
  return { r, g, b };
}
